import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTranslate} from '@computerrock/formation-i18n';
import {useStyles, TextAreaField, SelectField, Option, Modal, ButtonSecondary} from '@ace-de/ui-components';
import {InteractiveIcon, closeIcon} from '@ace-de/ui-components/icons';
import {efDamageNoteTopics} from '@ace-de/eua-entity-types';
import * as savActionTypes from '../savActionTypes';
import config from '../../config';

const DamageNoteModal = props => {
    const {cx} = useStyles();
    const {translate, createTranslateShorthand} = useTranslate();
    const translateModal = createTranslateShorthand('damage_note_modal');
    const {serviceAssignments, hasBackdrop, location} = props;
    const {submitSAVDamageDescriptionForm, declineSAVDamageDescriptionForm} = props;
    const textTemplateTopicRef = useRef(false);
    const [textTemplateTopic, setTextTemplateTopic] = useState();
    const [limitExceededError, setLimitExceededError] = useState([]);
    const serviceCaseId = location?.query?.serviceCaseId;
    const lineNo = location?.query?.lineNo;
    const serviceAssignment = serviceAssignments[`${serviceCaseId}-${lineNo}`];
    const [damageNote, setDamageNote] = useState(serviceAssignment?.additionalDamageDescription || '');
    const damageNoteRef = useRef(damageNote);

    const formatDamageNoteText = useCallback((damageNote, selectedTopic) => {
        if (!selectedTopic) return;
        const translatedTopic = translateModal(`to_note_panel_text_template.${selectedTopic.toLowerCase()}`);

        if (translatedTopic.length + damageNote.length > config.DAMAGE_NOTE_MAX_LENGTH) {
            setLimitExceededError([translateModal(`to_note_panel_error_message.input_too_long`)]);
            return damageNote;
        }

        setLimitExceededError([]);

        if (!damageNote) return translatedTopic;

        return damageNote + '\n\n' + translatedTopic;
    }, [translateModal]);

    useEffect(() => {
        if (serviceAssignment?.additionalDamageDescription
            && serviceAssignment?.additionalDamageDescription !== damageNoteRef.current) {
            damageNoteRef.current = serviceAssignment?.additionalDamageDescription;
            setDamageNote(serviceAssignment?.additionalDamageDescription);
        }
    }, [serviceAssignment?.additionalDamageDescription, damageNoteRef, setDamageNote]);

    useEffect(() => {
        if (textTemplateTopic) {
            textTemplateTopicRef.current = true;
        }
    }, [textTemplateTopic]);

    useEffect(() => {
        if (textTemplateTopicRef.current) {
            setDamageNote(formatDamageNoteText(damageNote, textTemplateTopic));
            textTemplateTopicRef.current = false;
        }
    }, [damageNote, setDamageNote, formatDamageNoteText, textTemplateTopic]);

    const handleOnChange = value => {
        setDamageNote(value);

        if (damageNote.length > config.DAMAGE_NOTE_MAX_LENGTH) {
            setLimitExceededError([translateModal(`to_note_panel_error_message.input_too_long`)]);
            return;
        }

        setLimitExceededError([]);
    };

    const handleOnSubmit = () => {
        const serviceAssignmentData = {
            additionalDamageDescription: damageNote,
        };
        submitSAVDamageDescriptionForm({
            serviceAssignmentData,
            assignmentType: serviceAssignment.assignmentType,
            serviceAssignmentLineNo: lineNo,
        });
    };

    return (
        <Modal
            hasBackdrop={hasBackdrop}
            action={(
                <InteractiveIcon
                    icon={closeIcon}
                    onClick={declineSAVDamageDescriptionForm}
                />
            )}
            title={translateModal('panel_title.to_note')}
            contentClassName={cx([
                'global!ace-u-modal-content-size--xs',
                'global!ace-u-margin--top-24',
                'global!ace-u-flex',
                'global!ace-u-flex--direction-column',
                'global!ace-u-flex--align-center',
                'global!ace-u-flex--justify-center',
            ])}
        >
            <SelectField
                name="topic"
                label={translateModal('to_note_panel_label.topic')}
                placeholder={translate('global.select.placeholder')}
                className={cx(['global!ace-u-margin--bottom-32', 'global!ace-u-full-width'])}
                onChange={setTextTemplateTopic}
            >
                {Object.values(efDamageNoteTopics).map((topic, id) => (
                    <Option
                        key={`${topic}-${id}`}
                        name={`${topic}-${id}`}
                        value={topic}
                    >
                        {translateModal(`to_note_panel_option.${topic.toLowerCase()}`)}
                    </Option>
                ))}
            </SelectField>
            <TextAreaField
                name="additionalDescription"
                value={damageNote}
                placeholder={translateModal('input_placeholder.enter_description')}
                label={translateModal('input_label.free_commissioning_text')}
                isResizable={false}
                onChange={handleOnChange}
                maxLength={config.DAMAGE_NOTE_MAX_LENGTH}
                className={cx('global!ace-u-full-width')}
                contentClassName={cx('ace-c-text-area--medium')}
                errors={limitExceededError}
            />
            <p
                className={cx([
                    'global!ace-u-margin--top-8',
                    'global!ace-u-typography--variant-caption',
                    'global!ace-u-typography--color-medium-emphasis',
                ])}
            >
                {`${damageNote
                    ? damageNote.length
                    : 0
                }/${config.DAMAGE_NOTE_MAX_LENGTH}
                 ${translate('global.unit.characters')}`
                }
            </p>
            <div
                className={cx([
                    'global!ace-u-width--full',
                    'global!ace-u-flex',
                    'global!ace-u-flex--align-center',
                    'global!ace-u-flex--justify-flex-end',
                ])}
            >
                <ButtonSecondary
                    name="submitDamageNoteUpdate"
                    onClick={handleOnSubmit}
                    className={cx('global!ace-u-margin--top-24')}
                >
                    {translateModal('button_label.submit')}
                </ButtonSecondary>
            </div>
        </Modal>
    );
};

DamageNoteModal.propTypes = {
    hasBackdrop: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    serviceAssignments: PropTypes.object,
    submitSAVDamageDescriptionForm: PropTypes.func.isRequired,
    declineSAVDamageDescriptionForm: PropTypes.func.isRequired,
};

DamageNoteModal.defaultProps = {
    serviceAssignments: null,
};

const mapStateToProps = state => ({
    serviceAssignments: state.serviceAssignments.serviceAssignments,
});

const mapDispatchToProps = dispatch => ({
    submitSAVDamageDescriptionForm: payload => dispatch({
        type: savActionTypes.SUBMIT_SAV_DAMAGE_DESCRIPTION_FORM,
        payload,
    }),
    declineSAVDamageDescriptionForm: () => dispatch({
        type: savActionTypes.DECLINE_SAV_DAMAGE_DESCRIPTION_FORM,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(DamageNoteModal);
